/**
 * @file index.tsx
 *
 * @fileoverview Renders a set of cards based on a XML Feed.
 */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import AnchorDiv from '~/jobiqo/components/AnchorDiv';
import { RssFeedParagraph, RssItemParagraph } from '~/jobiqo/types/types';
import styles from './feedCards.module.scss';
import { Box } from '~/vcl/atoms/07-box';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { Picture } from '~/vcl/atoms/09-picture';
import { LinkButton } from '~/vcl/atoms/01-forms/01-button/02-link-button';
import { ExitIcon } from '~/vcl/icons/Exit';

interface RSSFeedCardsProps {
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
  row: RssFeedParagraph;
}

// Teaser cover sizes.
const teaserCoverHeight = '300';
const teaserCoverWidth = '600';

export const RSSFeedCards = ({ maxContainerSize, row }: RSSFeedCardsProps) => {
  return (
    <Box
      boxShadow="none"
      borderRadius="0px"
      marginTop={0}
      paddingTop={row.spacing ?? 12}
      paddingBottom={row.spacing ?? 12}
      backgroundColor={'gray0'}
    >
      <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
        {row.rssFeedSubTitle && (
          <>
            <Text color="primary" fontWeight="bold" textTransform="uppercase">
              {row.rssFeedSubTitle}
            </Text>
            <Spacing size="medium" />
          </>
        )}
        {row.rssFeedTitle && (
          <>
            <Text tag="h2" fontSize={[5, 6]} lineHeight={[5, 6]}>
              {row.rssFeedTitle}
            </Text>
            <Spacing size="large" />
          </>
        )}
        {(row as any).feedsItems && (row as any).feedsItems.length > 0 && (
          <Grid columns={[1, 2, 3]} gap={7}>
            {(row as any).feedsItems.map(
              (item: RssItemParagraph, index: number) => {
                return (
                  <Cell display="grid" key={index}>
                    <AnchorDiv route={item.link?.url ?? ''} target="_blank">
                      <Box className={styles.cardBox} paddingClean={true}>
                        {item?.image?.url ? (
                          <Picture
                            landscapeSrc={item?.image?.url}
                            title={item.title ?? ''}
                            alt={item.title ?? ''}
                          />
                        ) : (
                          <div
                            className={styles.banner}
                            style={{
                              aspectRatio: `${teaserCoverWidth} / ${teaserCoverHeight}`,
                              background: `var(--color-${row.color}`,
                            }}
                          />
                        )}
                        <Spacing margin={0} padding={5}>
                          <Text tag="h3" fontSize={4} lineHeight={4}>
                            {item.title}
                          </Text>
                          <Spacing />
                          {item.link?.url && (
                            <LinkButton
                              buttonStyle="primary"
                              raised={false}
                              href={item.link?.url}
                              rel="noopener noreferrer"
                              {...{ target: '_blank' }}
                            >
                              <ExitIcon title="New tab" />
                              <span>
                                <FormattedMessage
                                  id="feed_card_landing_view"
                                  defaultMessage="View more"
                                />
                              </span>
                            </LinkButton>
                          )}
                        </Spacing>
                      </Box>
                    </AnchorDiv>
                  </Cell>
                );
              },
            )}
          </Grid>
        )}
      </Main>
    </Box>
  );
};
export default RSSFeedCards;
